import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import styles from './styles.module.scss'

import { getInvoice } from '../../../api'
import { previewInvoicePdfUrl, downloadInvoicePdf } from '../../../helpers/invoice'
import { useQuery } from '../../../hooks/useQuery'

const formatCurrencyAmount = value => {
  const parsed = Number(value)

  if (Number.isNaN(parsed)) {
    return 0
  }

  return parsed
}

const mapInvoiceToPdfContext = invoice => {
  const items = Array.isArray(invoice?.items) ? invoice.items : []

  const lines = items.map((item, index) => ({
    id: item.id || index,
    sourceType: item.type,
    description: item.description || '-',
    quantity: Number(item.quantity) || 0,
    unit: '',
    taxRate: Number(item.vat) || 0,
    unitPrice: formatCurrencyAmount(item.unit_price),
    total: formatCurrencyAmount(item.total_price),
  }))

  const subtotalLabor = items
    .filter(item => item.type === 'work_time')
    .reduce((acc, item) => acc + formatCurrencyAmount(item.total_price), 0)

  const subtotalExpenses = items
    .filter(item => item.type === 'expense')
    .reduce((acc, item) => acc + formatCurrencyAmount(item.total_price), 0)

  const taxTotal = items.reduce((acc, item) => {
    return acc + (Number(item.vat)
      ? (formatCurrencyAmount(item.total_price) - (formatCurrencyAmount(item.unit_price) * formatCurrencyAmount(item.quantity)))
      : 0)
  }, 0)

  const grandTotal = formatCurrencyAmount(invoice.total_amount)

  return {
    invoice: {
      invoiceNumber: invoice.invoice_number,
      status: invoice.status,
      invoiceDate: invoice.invoice_date,
      dueDate: invoice.due_date,
      periodStart: invoice.invoice_date,
      periodEnd: invoice.due_date,
      currency: invoice.currency || 'EUR',
      paymentTerms: invoice.payment_terms || '-',
      notes: invoice.notes || '',
      subtotalLabor,
      subtotalExpenses,
      subtotalManual: 0,
      discountTotal: 0,
      taxTotal,
      grandTotal,
      lines,
    },
    customer: {
      id: invoice.customer?.id,
      name: invoice.customer?.display_name || invoice.customer?.legal_name || '-',
      email: invoice.customer?.billing_email || '',
      address: invoice.customer?.billing_address_line_1 || '',
      vatNumber: invoice.customer?.vat_number,
    },
    project: {
      name: invoice.place?.name || '-',
      code: invoice.place?.id ? `PL-${invoice.place.id}` : '',
    },
    template: {
      id: 'classic',
      name: 'Classic Professional',
      accent: '#3b3b80',
    },
  }
}

const ViewInvoice = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { id: companyId } = useParams()
  const { id: invoiceId, download } = useQuery(['id', 'download'])

  const [invoice, setInvoice] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadInvoice = async () => {
      if (!invoiceId) {
        setLoading(false)
        return
      }

      setLoading(true)
      const res = await getInvoice(companyId, invoiceId)
      setInvoice(res || null)
      setLoading(false)
    }

    loadInvoice()
  }, [companyId, invoiceId])

  const pdfContext = useMemo(() => {
    if (!invoice) {
      return null
    }

    return mapInvoiceToPdfContext(invoice)
  }, [invoice])

  useEffect(() => {
    let isMounted = true

    const buildPreview = async () => {
      if (!pdfContext) {
        setPreviewUrl('')
        return
      }

      const url = await previewInvoicePdfUrl(pdfContext)

      if (isMounted) {
        setPreviewUrl(url)
      }

      if (download && isMounted) {
        await downloadInvoicePdf(pdfContext)
      }
    }

    buildPreview()

    return () => {
      isMounted = false
    }
  }, [pdfContext, download])

  const handleBack = () => {
    history.push({ search: '?tab=invoices' })
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>{t('Loading invoice preview...')}</div>
      </div>
    )
  }

  if (!invoice || !pdfContext) {
    return (
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <button type='button' className={styles.backButton} onClick={handleBack}>{t('Back')}</button>
        </div>
        <div className={styles.emptyState}>{t('Invoice not found')}</div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <button type='button' className={styles.backButton} onClick={handleBack}>{t('Back')}</button>
        <div className={styles.title}>{invoice.invoice_number}</div>
      </div>
      {
        previewUrl
          ? <iframe title={t('Invoice preview')} src={previewUrl} className={styles.previewFrame} />
          : <div className={styles.emptyState}>{t('Unable to render preview')}</div>
      }
    </div>
  )
}

export default ViewInvoice
